import { Header } from './Header';
import { Bar, Pie } from 'react-chartjs-2';
import { ExternalLink, Shield, Lock, UserCheck, Brain, FileCheck, AlertTriangle, ChevronDown } from 'lucide-react';
import { useState } from 'react';

const POLICY_SECTIONS = [
  { id: 'purpose', label: 'Purpose & scope' },
  { id: 'security', label: 'Data security & governance' },
  { id: 'fair-use', label: 'Fair & ethical use' },
  { id: 'insights', label: 'Industry insights' },
  { id: 'innovation', label: 'Innovation framework' },
  { id: 'references', label: 'References & sources' },
];

export function AIPolicyPage() {
  const [expandedSections, setExpandedSections] = useState<string[]>(['purpose']);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]
    );
  };

  const adoptionData = {
    labels: ['Performance Analysis', 'Data Analysis', 'Administrative Tasks', 'Strategic Decision Support'],
    datasets: [
      {
        data: [28, 26, 25, 20],
        backgroundColor: ['rgba(0, 115, 255, 0.85)', 'rgba(0, 115, 255, 0.6)', 'rgba(0, 115, 255, 0.4)', 'rgba(0, 115, 255, 0.25)'],
        borderColor: 'rgba(255, 255, 255, 0.15)',
        borderWidth: 1,
      },
    ],
  };

  const benefitsData = {
    labels: ['Improved Project Outcomes', 'Enhanced Decision Making', 'Increased Efficiency', 'Other Benefits'],
    datasets: [
      {
        data: [70, 65, 60, 45],
        backgroundColor: ['rgba(0, 115, 255, 0.85)', 'rgba(0, 115, 255, 0.6)', 'rgba(0, 115, 255, 0.4)', 'rgba(0, 115, 255, 0.25)'],
        borderColor: 'rgba(255, 255, 255, 0.15)',
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: { color: 'rgba(255, 255, 255, 0.7)', padding: 20, font: { size: 11 } },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { color: 'rgba(255, 255, 255, 0.5)', font: { size: 11 } },
        grid: { color: 'rgba(255, 255, 255, 0.08)' },
      },
      x: {
        ticks: { color: 'rgba(255, 255, 255, 0.5)', font: { size: 11 } },
        grid: { color: 'rgba(255, 255, 255, 0.08)' },
      },
    },
  };

  const accordionShell = (id: string, index: number, label: string, body: React.ReactNode) => {
    const open = expandedSections.includes(id);
    return (
      <div key={id} className="border-t border-white/10">
        <button
          onClick={() => toggleSection(id)}
          className="w-full py-7 flex items-center justify-between text-left hover:opacity-90 transition-opacity group"
        >
          <div className="flex items-baseline gap-6">
            <span className="eyebrow text-primary">{String(index + 1).padStart(2, '0')}</span>
            <span className="text-xl md:text-2xl font-bold text-white group-hover:text-primary transition-colors">
              {label}
            </span>
          </div>
          <ChevronDown
            className={`w-5 h-5 text-white/50 group-hover:text-primary transition-transform ${
              open ? 'rotate-180' : ''
            }`}
          />
        </button>
        {open && <div className="pb-12">{body}</div>}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-20 w-full">
        {/* HERO */}
        <div className="grid grid-cols-12 gap-x-6 mb-16 lg:mb-20">
          <div className="col-span-12 lg:col-span-9">
            <p className="eyebrow text-primary mb-6">AI Policy · Responsible AI</p>
            <h1 className="display-lg text-white mb-6">
              How we build and run AI for project controls.
            </h1>
            <p className="text-lg text-white/65 max-w-3xl leading-snug">
              Movar Reply delivers project-controls, data, and digital services to high-value
              infrastructure in defence, utilities, energy, and rail. This policy sets the minimum
              standards for the safe, secure, and ethical use of AI across our operations and
              client engagements.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-3 lg:text-right mt-6 lg:mt-0">
            <p className="eyebrow text-white/40 mb-2">Sections</p>
            <p className="font-mono text-4xl md:text-5xl font-bold text-white">
              {String(POLICY_SECTIONS.length).padStart(2, '0')}
            </p>
          </div>
        </div>

        <div className="hairline mb-2" />

        {/* ACCORDIONS */}
        {accordionShell('purpose', 0, 'Purpose & scope',
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-4 h-4 text-primary" />
                <p className="eyebrow text-white/70">Core principles</p>
              </div>
              <ul className="space-y-2 text-white/75">
                {['Preserves client trust and confidentiality', 'Complies with applicable law and regulation', 'Aligns with recognised ethical principles'].map((t) => (
                  <li key={t} className="flex items-baseline gap-2"><span className="text-primary">·</span><span>{t}</span></li>
                ))}
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Brain className="w-4 h-4 text-primary" />
                <p className="eyebrow text-white/70">AI systems covered</p>
              </div>
              <ul className="space-y-2 text-white/75">
                {['Generative LLM services via Azure OpenAI', 'ML models in project-controls tools', 'RPA bots with ML capabilities', 'AI-powered decision support'].map((t) => (
                  <li key={t} className="flex items-baseline gap-2"><span className="text-primary">·</span><span>{t}</span></li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {accordionShell('security', 1, 'Data security & governance',
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Lock className="w-4 h-4 text-primary" />
                <p className="eyebrow text-white/70">Data protection</p>
              </div>
              <ul className="space-y-2 text-white/75">
                {['Azure-managed keys (AES-256)', 'TLS v1.2+ for data in transit', 'RBAC with least-privilege'].map((t) => (
                  <li key={t} className="flex items-baseline gap-2"><span className="text-primary">·</span><span>{t}</span></li>
                ))}
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-4">
                <FileCheck className="w-4 h-4 text-primary" />
                <p className="eyebrow text-white/70">Compliance</p>
              </div>
              <ul className="space-y-2 text-white/75">
                {['UK GDPR & Data Protection Act 2018', 'ISO/IEC 27001:2022', 'NCSC Cloud Security Principles'].map((t) => (
                  <li key={t} className="flex items-baseline gap-2"><span className="text-primary">·</span><span>{t}</span></li>
                ))}
              </ul>
            </div>
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-4 h-4 text-primary" />
                <p className="eyebrow text-white/70">Critical security measures</p>
              </div>
              <ul className="space-y-2 text-white/75">
                {[
                  "All production AI workloads run inside Movar Reply's Azure subscription in UK/EU regions",
                  'Private link endpoints block public-internet access',
                  'Privileged access requires PIM and MFA',
                  '24-month immutable audit logging',
                ].map((t) => (
                  <li key={t} className="flex items-baseline gap-2"><span className="text-primary">·</span><span>{t}</span></li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {accordionShell('fair-use', 2, 'Fair & ethical use',
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <UserCheck className="w-4 h-4 text-primary" />
                <p className="eyebrow text-white/70">Permitted uses</p>
              </div>
              <ul className="space-y-2 text-white/75">
                {['Project planning insights', 'Schedule risk simulation', 'Cost-estimate generation', 'Document summarisation'].map((t) => (
                  <li key={t} className="flex items-baseline gap-2"><span className="text-primary">·</span><span>{t}</span></li>
                ))}
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <p className="eyebrow text-white/70">Prohibited uses</p>
              </div>
              <ul className="space-y-2 text-white/75">
                {['Autonomous safety decisions', 'Deepfakes or deception', 'Unauthorised surveillance', 'Public LLM data sharing'].map((t) => (
                  <li key={t} className="flex items-baseline gap-2"><span className="text-red-400">·</span><span>{t}</span></li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {accordionShell('insights', 3, 'Industry insights',
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <p className="eyebrow text-white/70 mb-4">AI adoption in project management</p>
                <div className="h-[280px]">
                  <Bar data={adoptionData} options={chartOptions} />
                </div>
              </div>
              <div>
                <p className="eyebrow text-white/70 mb-4">Benefits of AI implementation</p>
                <div className="h-[280px]">
                  <Pie data={benefitsData} options={chartOptions} />
                </div>
              </div>
            </div>
            <p className="text-white/70 leading-relaxed max-w-3xl border-l-2 border-primary pl-5 italic">
              According to recent industry surveys, 70% of UK project managers have already
              integrated AI into their processes, with the majority reporting improved project
              outcomes. The UK's robust digital infrastructure and supportive regulatory
              environment position it as a leader in leveraging AI for infrastructure delivery.
            </p>
          </div>
        )}

        {accordionShell('innovation', 4, 'Innovation & adoption framework',
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-white/10">
            {[
              { stage: 'Explore', desc: 'Ideate & prototype rapidly in a segregated sandbox.' },
              { stage: 'Pilot', desc: 'Validate value & risk on limited scope.' },
              { stage: 'Scale', desc: 'Deploy to production environments.' },
              { stage: 'Evolve', desc: 'Continuous improvement & monitoring.' },
            ].map((step, i) => (
              <div key={step.stage} className="bg-[#131B30] p-6">
                <p className="font-mono text-sm text-primary mb-3">{String(i + 1).padStart(2, '0')}</p>
                <p className="text-lg font-bold text-white mb-2">{step.stage}</p>
                <p className="text-sm text-white/65 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        )}

        {accordionShell('references', 5, 'References & sources',
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
            <a
              href="https://www.apm.org.uk/news/seven-in-10-project-managers-have-benefited-from-the-implementation-of-artificial-intelligence-finds-latest-apm-survey/"
              target="_blank" rel="noopener noreferrer"
              className="bg-[#131B30] p-6 group hover:bg-[#1B2540] transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-white font-bold mb-1.5">Association for Project Management</p>
                  <p className="text-white/55 text-sm leading-relaxed">Seven in 10 project managers benefit from AI implementation</p>
                </div>
                <ExternalLink className="w-4 h-4 text-white/40 group-hover:text-primary transition-colors mt-1 shrink-0" />
              </div>
            </a>
            <a
              href="https://www.gov.uk/government/publications/data-analytics-and-ai-in-government-project-delivery"
              target="_blank" rel="noopener noreferrer"
              className="bg-[#131B30] p-6 group hover:bg-[#1B2540] transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-white font-bold mb-1.5">UK Government</p>
                  <p className="text-white/55 text-sm leading-relaxed">Data Analytics and AI in Government Project Delivery</p>
                </div>
                <ExternalLink className="w-4 h-4 text-white/40 group-hover:text-primary transition-colors mt-1 shrink-0" />
              </div>
            </a>
          </div>
        )}

        <div className="border-t border-white/10" />
      </main>

      <footer className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
          <p className="eyebrow text-white/40">&copy; 2026 Movar Reply</p>
          <p className="text-sm text-white/45 italic">big enough to deliver, small enough to care</p>
          <p className="eyebrow text-white/30 md:ml-auto">A Reply Group company</p>
        </div>
      </footer>
    </div>
  );
}
